import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Select,
  Button,
} from 'antd';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import type { GetQuestionsParams } from '@/types';
import { miniQuizService } from '@/services/miniQuizService';

const { Search } = Input;
const { Option } = Select;

interface QuestionsFiltersProps {
  filters: GetQuestionsParams;
  onFiltersChange: (filters: GetQuestionsParams) => void;
  onClear: () => void;
}

const QuestionsFilters: React.FC<QuestionsFiltersProps> = ({
  filters,
  onFiltersChange,
  onClear,
}) => {
  const [categories, setCategories] = useState<string[]>([]);

  // Load categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await miniQuizService.getCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Failed to load categories:', error);
        // Fallback to hardcoded categories
        setCategories(['30nam', 'megasale', 'myshop', 'tindung', 'xdigi']);
      }
    };

    loadCategories();
  }, []);
  const handleSearchChange = (value: string) => {
    onFiltersChange({
      ...filters,
      search: value || undefined,
      page: 1, // Reset to first page when searching
    });
  };

  const handleStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      isActive: value === 'all' ? undefined : value === 'active',
      page: 1,
    });
  };

  const handleCategoryChange = (value: string) => {
    onFiltersChange({
      ...filters,
      category: value === 'all' ? undefined : value,
      page: 1,
    });
  };

  const handleSortChange = (value: string) => {
    const [sortBy, sortOrder] = value.split('-') as [string, 'asc' | 'desc'];
    onFiltersChange({
      ...filters,
      sortBy: sortBy as 'createdAt' | 'updatedAt' | 'question',
      sortOrder,
      page: 1,
    });
  };

  const handleLimitChange = (value: number) => {
    onFiltersChange({
      ...filters,
      limit: value,
      page: 1,
    });
  };

  const getSortValue = () => {
    if (filters.sortBy && filters.sortOrder) {
      return `${filters.sortBy}-${filters.sortOrder}`;
    }
    return 'createdAt-desc';
  };

  const getStatusValue = () => {
    if (filters.isActive === undefined) return 'all';
    return filters.isActive ? 'active' : 'inactive';
  };

  const getCategoryValue = () => {
    return filters.category || 'all';
  };

  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={12} md={8} lg={5}>
          <Search
            placeholder="Search questions..."
            allowClear
            enterButton={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            onSearch={handleSearchChange}
          />
        </Col>
        
        <Col xs={12} sm={6} md={4} lg={3}>
          <Select
            style={{ width: '100%' }}
            placeholder="Status"
            value={getStatusValue()}
            onChange={handleStatusChange}
          >
            <Option value="all">All Status</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
        </Col>

        <Col xs={12} sm={6} md={4} lg={3}>
          <Select
            style={{ width: '100%' }}
            placeholder="Category"
            value={getCategoryValue()}
            onChange={handleCategoryChange}
          >
            <Option value="all">All Categories</Option>
            {categories.map((category) => (
              <Option key={category} value={category}>
                {category}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={12} sm={6} md={4} lg={3}>
          <Select
            style={{ width: '100%' }}
            placeholder="Sort by"
            value={getSortValue()}
            onChange={handleSortChange}
          >
            <Option value="createdAt-desc">Newest First</Option>
            <Option value="createdAt-asc">Oldest First</Option>
            <Option value="updatedAt-desc">Recently Updated</Option>
            <Option value="updatedAt-asc">Least Updated</Option>
            <Option value="question-asc">Question A-Z</Option>
            <Option value="question-desc">Question Z-A</Option>
          </Select>
        </Col>

        <Col xs={12} sm={6} md={4} lg={3}>
          <Select
            style={{ width: '100%' }}
            placeholder="Per page"
            value={filters.limit || 20}
            onChange={handleLimitChange}
          >
            <Option value={10}>10 per page</Option>
            <Option value={20}>20 per page</Option>
            <Option value={50}>50 per page</Option>
            <Option value={100}>100 per page</Option>
          </Select>
        </Col>

        <Col xs={12} sm={6} md={4} lg={2}>
          <Button
            icon={<ClearOutlined />}
            onClick={onClear}
            block
          >
            Clear Filters
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default QuestionsFilters;
